import React, { useState, useEffect } from "react";
import {
  Table, Button, Card, Row, Col, Select, Modal, Form, InputNumber, message, Progress, Popconfirm, Upload, Alert,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Api_Url } from "../../../config/config";
import axios from "axios";
import { ErrorToast, SuccessToast } from "../../ToastComponent";

// SuperAdmin can delete branch targets
const isSuperAdmin = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem("user"));
    return user?.roles?.includes("SuperAdmin") || false;
  } catch {
    return false;
  }
};

const { Option } = Select;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];

const attainment = (t, a) => {
  const tv = Number(t ?? 0);
  const av = Number(a ?? 0);
  return tv > 0 ? Math.round((av / tv) * 100) : 0;
};

export const BranchTargetManagement = () => {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchTargets, setBranchTargets] = useState([]); // Target definitions of type Branch
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    branchId: undefined,
    month: new Date().getMonth() + 1,
    year: currentYear,
  });

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Excel upload state
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState("");

  // AUM recalculation state
  const [aumReport, setAumReport] = useState("");
  const [recalculating, setRecalculating] = useState(false);

  const token = JSON.parse(sessionStorage.getItem("user"))?.accessToken;
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.branchId) params.append("branchId", filters.branchId);
      if (filters.month) params.append("month", filters.month);
      if (filters.year) params.append("year", filters.year);
      const res = await axios.get(`${Api_Url}api/v1/BranchTarget?${params.toString()}`, authHeader);
      setRows(res?.data?.data || []);
    } catch (e) {
      ErrorToast("Failed to fetch branch targets");
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${Api_Url}api/v1/Branch?PageNumber=1&PageSize=1000`, authHeader);
      setBranches(res?.data?.data || []);
    } catch (e) {
      // silent
    }
  };

  // Branch-type target definitions for the selected month/year (Type = 1 = Branch)
  const fetchBranchTargetDefs = async () => {
    try {
      const params = new URLSearchParams();
      params.append("type", 1);
      if (filters.month) params.append("month", filters.month);
      if (filters.year) params.append("year", filters.year);
      const res = await axios.get(`${Api_Url}api/v1/Target?${params.toString()}`, authHeader);
      setBranchTargets(res?.data?.data || []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    fetchRows();
    fetchBranchTargetDefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-recalculate achievement at 7 AM daily
  useEffect(() => {
    const scheduleNext7Am = () => {
      const now = new Date();
      const next7am = new Date();
      next7am.setHours(7, 0, 0, 0);
      if (now >= next7am) {
        next7am.setDate(next7am.getDate() + 1);
      }
      const msUntil7am = next7am - now;
      const timerId = setTimeout(() => {
        handleRecalculateAum();
        scheduleNext7Am(); // reschedule for next day
      }, msUntil7am);
      return timerId;
    };
    const timerId = scheduleNext7Am();
    return () => clearTimeout(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitAdd = async (values) => {
    setSaving(true);
    try {
      const payload = {
        BranchId: values.BranchId,
        TargetId: values.TargetId,
        TargetValue: values.TargetValue || 0,
        AchievementValue: values.AchievementValue || 0,
      };
      const res = await axios.post(`${Api_Url}api/v1/BranchTarget`, payload, authHeader);
      if (res.status === 200 || res.status === 201) {
        message.success("Branch target set");
        setIsAddOpen(false);
        addForm.resetFields();
        fetchRows();
      }
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to set branch target");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row) => {
    setSelectedRow(row);
    editForm.setFieldsValue({
      TargetValue: row.targetValue,
      AchievementValue: row.achievementValue,
    });
    setIsEditOpen(true);
  };

  const submitEdit = async (values) => {
    setSaving(true);
    try {
      const payload = {
        Id: selectedRow.id,
        TargetValue: values.TargetValue || 0,
        AchievementValue: values.AchievementValue || 0,
      };
      const res = await axios.put(`${Api_Url}api/v1/BranchTarget/${selectedRow.id}`, payload, authHeader);
      if (res.status === 200 || res.status === 201) {
        message.success("Branch target updated");
        setIsEditOpen(false);
        fetchRows();
      }
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to update branch target");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await axios.delete(`${Api_Url}api/v1/BranchTarget/${record.id}`, authHeader);
      SuccessToast("Branch target deleted");
      fetchRows();
    } catch (e) {
      ErrorToast(e.response?.data?.message || "Failed to delete branch target");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      message.warning("Please choose an Excel file first.");
      return;
    }
    setUploading(true);
    setReport("");
    try {
      const formData = new FormData();
      formData.append("ExcelFile", file);
      formData.append("Month", filters.month);
      formData.append("Year", filters.year);
      const res = await axios.post(
        `${Api_Url}api/v1/BranchTarget/upload-excel`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      SuccessToast(res?.data?.message || "Upload complete");
      setReport(res?.data?.data || "");
      setFile(null);
      fetchRows();
    } catch (e) {
      ErrorToast(e.response?.data?.message || "Upload failed");
      if (e.response?.data?.data) setReport(e.response.data.data);
    } finally {
      setUploading(false);
    }
  };

  const handleRecalculateAum = async () => {
    setRecalculating(true);
    setAumReport("");
    try {
      const params = new URLSearchParams();
      params.append("month", filters.month);
      params.append("year", filters.year);
      const res = await axios.post(
        `${Api_Url}api/v1/AumDisbursal/recalculate?${params.toString()}`,
        null,
        authHeader
      );
      SuccessToast(res?.data?.message || "Achievement recalculated");
      setAumReport(res?.data?.data || "");
      fetchRows();
    } catch (e) {
      ErrorToast(e.response?.data?.message || "Recalculate failed");
      if (e.response?.data?.data) setAumReport(e.response.data.data);
    } finally {
      setRecalculating(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ["Branch Name", "Target Name", "Target Value", "Achievement Value"];
    const sample = ["Durg", "AUM", "100", "0"];
    const csv = headers.join(",") + "\n" + sample.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "BranchTarget_Template.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const canDelete = isSuperAdmin();

  const columns = [
    { title: "ID", dataIndex: "id", width: 70, sorter: (a, b) => a.id - b.id, defaultSortOrder: "descend" },
    { title: "Branch", dataIndex: "branchName", sorter: (a, b) => (a.branchName || "").localeCompare(b.branchName || "") },
    { title: "Target Name", dataIndex: "targetName", sorter: (a, b) => (a.targetName || "").localeCompare(b.targetName || "") },
    { title: "Month", dataIndex: "month", render: (m) => MONTHS[m - 1] || m, sorter: (a, b) => (a.month || 0) - (b.month || 0) },
    { title: "Year", dataIndex: "year", sorter: (a, b) => (a.year || 0) - (b.year || 0) },
    { title: "Target (Cr.)", dataIndex: "targetValue", render: (v) => `₹ ${Number(v ?? 0).toLocaleString()} Cr.`, sorter: (a, b) => (a.targetValue || 0) - (b.targetValue || 0) },
    { title: "Achievement (Cr.)", dataIndex: "achievementValue", render: (v) => `₹ ${Number(v ?? 0).toLocaleString()} Cr.`, sorter: (a, b) => (a.achievementValue || 0) - (b.achievementValue || 0) },
    {
      title: "Attainment",
      key: "attainment",
      render: (_, r) => {
        const pct = attainment(r.targetValue, r.achievementValue);
        return <Progress percent={pct} size="small" status={pct >= 100 ? "success" : "active"} style={{ width: 140 }} />;
      },
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: canDelete ? 190 : 110,
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Button type="primary" ghost onClick={() => openEdit(record)}>
            Update
          </Button>
          {canDelete && (
            <Popconfirm
              title="Delete this branch target?"
              description="This action cannot be undone."
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              onConfirm={() => handleDelete(record)}
            >
              <Button danger ghost>Delete</Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row mb-3">
          <div className="col-12 d-flex align-items-center justify-content-between">
            <h3 className="mb-0">Branch Targets</h3>
            <button className="btn btn-primary" onClick={() => { addForm.resetFields(); setIsAddOpen(true); }}>
              <i className="fa-solid fa-plus me-2"></i> Set Branch Target
            </button>
          </div>
        </div>

        <Card className="mb-4">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8} md={6}>
              <Select
                placeholder="Filter by Branch"
                value={filters.branchId}
                onChange={(v) => setFilters((p) => ({ ...p, branchId: v }))}
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: "100%" }}
              >
                {branches.map((b) => (
                  <Option key={b.id} value={b.id}>{b.name}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Select value={filters.month} onChange={(v) => setFilters((p) => ({ ...p, month: v }))} style={{ width: "100%" }}>
                {MONTHS.map((m, i) => (<Option key={i + 1} value={i + 1}>{m}</Option>))}
              </Select>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Select value={filters.year} onChange={(v) => setFilters((p) => ({ ...p, year: v }))} style={{ width: "100%" }}>
                {YEARS.map((y) => (<Option key={y} value={y}>{y}</Option>))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Bulk upload */}
        <Card className="mb-4" size="small" title="Bulk Upload">
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Select
              value={filters.month}
              onChange={(v) => setFilters((p) => ({ ...p, month: v }))}
              style={{ width: 140 }}
            >
              {MONTHS.map((m, i) => (<Option key={i + 1} value={i + 1}>{m}</Option>))}
            </Select>
            <Select
              value={filters.year}
              onChange={(v) => setFilters((p) => ({ ...p, year: v }))}
              style={{ width: 100 }}
            >
              {YEARS.map((y) => (<Option key={y} value={y}>{y}</Option>))}
            </Select>
            <Upload
              beforeUpload={(f) => { setFile(f); return false; }}
              maxCount={1}
              fileList={file ? [file] : []}
              onRemove={() => setFile(null)}
              accept=".xlsx,.xls,.csv"
            >
              <Button icon={<UploadOutlined />}>Choose Excel</Button>
            </Upload>
            <Button type="primary" loading={uploading} onClick={handleUpload}>Upload</Button>
            <Button onClick={downloadTemplate}>Download Template</Button>
            <div style={{ width: 1, height: 24, background: "#d9d9d9", margin: "0 4px" }} />
            <Button loading={recalculating} onClick={handleRecalculateAum}>
              Recalculate ({MONTHS[filters.month - 1]} {filters.year})
            </Button>
          </div>
          <Alert
            className="mt-3"
            type="info"
            showIcon
            message="Columns: Branch Name | Target Name | Target Value | Achievement Value. The Target must already exist (Branch type) for the selected month/year. Existing branch+target rows are updated. Accepts .csv, .xlsx, .xls"
          />
          {(report || aumReport) && (
            <pre style={{ whiteSpace: "pre-wrap", marginTop: 12, marginBottom: 0, maxHeight: 220, overflow: "auto" }}>
              {report || aumReport}
            </pre>
          )}
        </Card>

        <div className="table-responsive">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={rows}
            rowKey="id"
            loading={loading}
            pagination={{ showSizeChanger: true, defaultPageSize: 10, pageSizeOptions: ["10", "20", "50", "100", "200"] }}
            scroll={{ x: 1100 }}
          />
        </div>

        {/* Set Branch Target modal */}
        <Modal
          title={<span style={{ fontSize: 18, fontWeight: "bold" }}>Set Branch Target</span>}
          open={isAddOpen}
          onCancel={() => setIsAddOpen(false)}
          onOk={() => addForm.submit()}
          okText="Save"
          confirmLoading={saving}
          centered
          width={640}
        >
          <Form form={addForm} layout="vertical" onFinish={submitAdd}>
            <Form.Item name="BranchId" label="Branch" rules={[{ required: true, message: "Select a branch" }]}>
              <Select showSearch placeholder="Select branch" optionFilterProp="children"
                filterOption={(input, option) => (option.children || "").toLowerCase().includes(input.toLowerCase())}>
                {branches.map((b) => (<Option key={b.id} value={b.id}>{b.name}</Option>))}
              </Select>
            </Form.Item>
            <Form.Item name="TargetId" label="Target" rules={[{ required: true, message: "Select a branch target" }]}
              extra={branchTargets.length === 0 ? "No Branch-type targets for this month/year. Create one under Target Management first." : undefined}>
              <Select showSearch placeholder="Select target" optionFilterProp="children"
                filterOption={(input, option) => (option.children || "").toLowerCase().includes(input.toLowerCase())}>
                {branchTargets.map((t) => (<Option key={t.id} value={t.id}>{t.name}</Option>))}
              </Select>
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="TargetValue" label="Target Value" rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="AchievementValue" label="Achievement Value">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* Edit Branch Target modal */}
        <Modal
          title={<span style={{ fontSize: 18, fontWeight: "bold" }}>Update Branch Target</span>}
          open={isEditOpen}
          onCancel={() => setIsEditOpen(false)}
          onOk={() => editForm.submit()}
          okText="Update"
          confirmLoading={saving}
          centered
          width={560}
        >
          <p className="text-muted">
            {selectedRow?.branchName} — <b>{selectedRow?.targetName}</b>
          </p>
          <Form form={editForm} layout="vertical" onFinish={submitEdit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="TargetValue" label="Target Value" rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="AchievementValue" label="Achievement Value">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </div>
  );
};
